import { useQuery } from '@tanstack/react-query';
import { Icoon } from '../ui/Icoon';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BORD_KOLOMMEN, ITEM_STATUS_LABEL, agileVoortgang, fmtDag, fmtUren, inEenheid, type AgilePrognose, type ItemStatus } from '@ep/core';
import { api } from '../lib/api';
import { useActie, useData, useMag, type TakenpakketRij } from '../lib/data';
import { Badge, Balk, Laden, Modal, Tabs, Veld, VerwijderKnop } from '../ui/ui';
import { TakenpakketForm } from './Werkpakketten';
import { Stand } from '../ui/Uren';

interface Item extends TakenpakketRij {
  status: ItemStatus;
  sprintId: string | null;
  eigenaarId: string | null;
}
interface Sprint {
  id: string;
  nummer: number;
  start: string;
  eind: string;
  doel: string | null;
  status: 'GEPLAND' | 'LOPEND' | 'AFGESLOTEN';
  leden: { engineerId: string; inzetPct: number }[];
  capaciteit: { totaal: number; bruto: number; perLid: { engineerId: string; uren: number }[] };
  gepland: number;
  geplandUren: number;
  kleur: 'groen' | 'geel' | 'rood';
  tijd?: { besteed: number; rest: number; afwijking: number };
  notitie: string | null;
  rapport: Rapport | null;
  burndown: { totaal: number; punten: { dag: string; ideaal: number; rest: number | null }[] };
}
interface Rapport {
  afgesloten: string;
  eenheid: 'UREN' | 'PUNTEN';
  doel: string | null;
  team: { naam: string; inzetPct: number }[];
  capaciteitUren: number | null;
  gepland: number;
  klaar: number;
  klaarItems: { naam: string; waarde: number }[];
  nietAf: { naam: string; waarde: number; status: string }[];
  naar: string;
  notitie: string | null;
  burndown: Sprint['burndown'] | null;
}
interface Moment { id: string; sprintId: string; titel: string; startdatum: string; einddatum: string; van: string; tot: string; sync: string; syncFout: string | null }
interface AgileData {
  werkpakket: { id: string; naam: string; deadline: string | null; sprintWeken: number; focusfactor: number; standup: boolean; schatting: 'UREN' | 'PUNTEN'; focusUren: number };
  project: { id: string; nummer: string; naam: string };
  items: Item[];
  sprints: Sprint[];
  velocity: { waarde: number | null; sprints: { nummer: number; klaar: number }[] };
  prognose: AgilePrognose;
  momenten: Moment[];
}

const KLEUR = { groen: 'groen', geel: 'oranje', rood: 'rood' } as const;
const STATUS_TOON: Record<ItemStatus, 'grijs' | 'accent' | 'blauw' | 'oranje' | 'groen'> = { BACKLOG: 'grijs', GEPLAND: 'accent', BEZIG: 'blauw', REVIEW: 'oranje', KLAAR: 'groen' };

/** SPEC §9 – backlog, sprintplanning en bord van één agile werkpakket. */
export function Agile() {
  const { wpId } = useParams();
  const { data: d } = useData();
  const mag = useMag();
  const kan = mag('agile.plannen');
  const q = useQuery({ queryKey: ['agile', wpId], queryFn: () => api.get<AgileData>(`/api/agile/${wpId}`) });
  const [tab, setTab] = useState<'backlog' | 'sprint' | 'bord'>('bord');
  const [sprintSel, setSprintSel] = useState<string | null>(null);
  const [itemForm, setItemForm] = useState<Item | 'nieuw' | null>(null);
  const [sprintForm, setSprintForm] = useState<Sprint | null>(null);
  const [afsluiten, setAfsluiten] = useState<Sprint | null>(null);
  const ververs = { opNa: () => void q.refetch() };
  const zet = useActie((a: { id: string; body: Record<string, unknown> }) => api.patch(`/api/items/${a.id}`, a.body), ververs);
  const volgorde = useActie((ids: string[]) => api.post(`/api/agile/${wpId}/volgorde`, { ids }), ververs);
  const nieuweSprint = useActie(() => api.post<{ id: string }>(`/api/agile/${wpId}/sprints`, {}), { succes: 'Sprint aangemaakt', opNa: (r) => { setSprintSel(r.id); setTab('sprint'); void q.refetch(); } });
  const [startSprint, setStartSprint] = useState<Sprint | null>(null);
  const [template, setTemplate] = useState(false);
  const [rapport, setRapport] = useState<Sprint | null>(null);
  const verwijderSprint = useActie((id: string) => api.del(`/api/sprints/${id}`), { succes: 'Sprint verwijderd', opNa: () => { setSprintSel(null); void q.refetch(); } });
  const verwijderItem = useActie((id: string) => api.del(`/api/takenpakketten/${id}`), { succes: 'Item verwijderd', ...ververs });

  const a = q.data;
  const actief = a?.sprints.filter((s) => s.status !== 'AFGESLOTEN') ?? [];
  const lopend = a?.sprints.find((s) => s.status === 'LOPEND');
  useEffect(() => {
    if (!a || sprintSel) return;
    const kies = lopend ?? actief.at(0);
    if (kies) setSprintSel(kies.id);
  }, [a, sprintSel, lopend, actief]);
  if (!a || !d) return <Laden />;

  const naam = (id: string | null) => d.engineers.find((e) => e.id === id)?.naam ?? '–';
  const sprint = a.sprints.find((s) => s.id === sprintSel) ?? lopend ?? actief.at(0) ?? null;
  const backlog = a.items.filter((i) => !i.sprintId && i.status === 'BACKLOG');
  const inSprint = sprint ? a.items.filter((i) => i.sprintId === sprint.id) : [];
  const pt = a.werkpakket.schatting === 'PUNTEN';
  const e = pt ? 'pt' : 'u';
  const sch = (i: Item) => (pt ? `${i.punten ?? '?'} pt · ${fmtUren(i.uren)}u` : `${fmtUren(i.uren)}u`);
  const waarde = (i: Item) => (pt ? (i.punten ?? 0) : i.uren);
  const v = agileVoortgang(inEenheid(a.items, a.werkpakket.schatting));
  const schuif = (i: number, r: number) => {
    const ids = backlog.map((x) => x.id);
    [ids[i], ids[i + r]] = [ids[i + r], ids[i]];
    volgorde.mutate([...ids, ...a.items.filter((x) => !backlog.includes(x)).map((x) => x.id)]);
  };

  return (
    <div className="pagina kolom" style={{ gap: 16 }}>
      <div className="rij wrap">
        <Link to={`/projecten/${a.project.id}/werkpakketten`} className="knop"><Icoon naam="arrow-left" maat={16} /> {a.project.nummer}</Link>
        <h1 className="groei">{a.werkpakket.naam} <span className="mute klein">· agile</span></h1>
        {kan && <button className="knop" onClick={() => setTemplate(true)}>Uit template</button>}
        {kan && <button className="knop" onClick={() => setItemForm('nieuw')}>+ Item</button>}
        {kan && <button className="knop primair" onClick={() => nieuweSprint.mutate()}>+ Sprint</button>}
      </div>
      <div className="kaartjes">
        <div className="kaartje"><div className="getal">{backlog.length}</div><div className="lbl">items in backlog</div></div>
        <div className="kaartje"><div className="getal">{lopend ? `Sprint ${lopend.nummer}` : '–'}</div><div className="lbl">{lopend ? `loopt t/m ${fmtDag(lopend.eind)}` : 'geen lopende sprint'}</div></div>
        <div className="kaartje"><div className="getal">{fmtUren(v.klaar)} / {fmtUren(v.totaal)}{e}</div><div className="lbl">klaar ({v.pct}%)</div></div>
        <div className="kaartje" title={a.velocity.sprints.map((x) => `Sprint ${x.nummer}: ${fmtUren(x.klaar)}${e} klaar`).join('\n') || 'Nog geen afgesloten sprint'}>
          <div className="getal">{a.velocity.waarde != null ? `${fmtUren(a.velocity.waarde)}${e}` : '–'}</div>
          <div className="lbl">velocity (gem. laatste {a.velocity.sprints.length || 3} sprints)</div>
        </div>
        <div className={`kaartje${a.prognose.teLaat ? ' rood' : ''}`} title={a.prognose.tempo ? `Nog ${fmtUren(a.prognose.rest)}${e} ÷ ${fmtUren(a.prognose.tempo)}${e} per sprint (${a.prognose.bron === 'velocity' ? 'velocity' : 'capaciteit, nog geen afgesloten sprint'}) = ${a.prognose.sprintsNodig} sprint(s)` : pt ? 'Nog geen tempo: in punten is er pas een prognose na de eerste afgesloten sprint' : 'Nog geen tempo: plan een sprint met een team'}>
          <div className="getal" style={a.prognose.teLaat ? { color: 'var(--rood)' } : undefined}>{a.prognose.eind ? fmtDag(a.prognose.eind, 'jaar') : '–'}</div>
          <div className="lbl">verwacht klaar{a.prognose.sprintsNodig ? ` · ${a.prognose.sprintsNodig} sprint(s)` : ''}{a.werkpakket.deadline ? ` · deadline ${fmtDag(a.werkpakket.deadline)}` : ''}</div>
        </div>
      </div>
      <div className="paneel kolom">
        <div className="rij wrap">
          <Tabs waarde={tab} onChange={setTab} opties={[['bord', 'Bord'], ['sprint', 'Sprintplanning'], ['backlog', `Backlog (${backlog.length})`]]} />
          <div className="groei" />
          {tab !== 'backlog' && a.sprints.length > 0 && (
            <select value={sprint?.id ?? ''} onChange={(e) => setSprintSel(e.target.value)} style={{ width: 'auto' }}>
              {a.sprints.map((s) => <option key={s.id} value={s.id}>Sprint {s.nummer} · {fmtDag(s.start)} – {fmtDag(s.eind)} · {s.status === 'LOPEND' ? 'loopt' : s.status === 'GEPLAND' ? 'gepland' : 'afgesloten'}</option>)}
            </select>
          )}
        </div>

        {tab !== 'backlog' && sprint && (
          <div className="paneel vlak kolom" style={{ gap: 6 }}>
            <div className="rij wrap">
              <b>Sprint {sprint.nummer}</b>
              <span className="klein mute">{fmtDag(sprint.start, 'jaar')} – {fmtDag(sprint.eind, 'jaar')}</span>
              {sprint.doel && <span className="klein">· doel: {sprint.doel}</span>}
              <div className="groei" />
              {pt ? (
                <Badge toon={KLEUR[sprint.kleur]} title={`${fmtUren(sprint.geplandUren)}u van ${fmtUren(sprint.capaciteit.totaal)}u capaciteit`}>
                  {sprint.gepland} pt gepland{a.velocity.waarde != null ? ` · velocity ${fmtUren(a.velocity.waarde)} pt` : ''}
                </Badge>
              ) : (
                <Badge toon={KLEUR[sprint.kleur]}>{fmtUren(sprint.gepland)} / {fmtUren(sprint.capaciteit.totaal)}u gepland</Badge>
              )}
              {sprint.rapport && <button className="knop klein" onClick={() => setRapport(sprint)}><Icoon naam="file-text" maat={14} /> Rapport</button>}
              {kan && sprint.status !== 'AFGESLOTEN' && <button className="knop klein" onClick={() => setSprintForm(sprint)}>Team en data</button>}
              {kan && sprint.status === 'GEPLAND' && <button className="knop primair klein" onClick={() => setStartSprint(sprint)}>Sprint starten</button>}
              {kan && sprint.status === 'LOPEND' && <button className="knop klein" onClick={() => setAfsluiten(sprint)}>Sprint afsluiten</button>}
              {kan && sprint.status === 'GEPLAND' && <VerwijderKnop klein label="🗑" onBevestig={() => verwijderSprint.mutate(sprint.id)} />}
            </div>
            <Balk waarde={pt ? sprint.geplandUren : sprint.gepland} max={sprint.capaciteit.totaal} />
            <div className="klein mute">
              Team: {sprint.leden.length ? sprint.leden.map((l) => `${naam(l.engineerId)}${l.inzetPct < 100 ? ` (${l.inzetPct}%)` : ''} ${fmtUren(sprint.capaciteit.perLid.find((x) => x.engineerId === l.engineerId)?.uren ?? 0)}u`).join(' · ') : 'nog geen teamleden'}
              {' '}· capaciteit na {a.werkpakket.focusfactor}% focusfactor
            </div>
            {sprint.tijd && sprint.status !== 'GEPLAND' && (
              <div className="klein">
                ⏱ <b>{fmtUren(sprint.tijd.besteed)}u</b> geschreven · nog <b>{fmtUren(sprint.tijd.rest)}u</b> te gaan
                {sprint.tijd.afwijking !== 0 && (
                  <span style={{ color: sprint.tijd.afwijking > 0 ? 'var(--rood)' : 'var(--groen)' }}> · {sprint.tijd.afwijking > 0 ? '+' : ''}{fmtUren(sprint.tijd.afwijking)}u tegenover de schatting</span>
                )}
              </div>
            )}
            {sprint.status !== 'GEPLAND' && sprint.burndown.totaal > 0 && <Burndown data={sprint.burndown} e={e} />}
            {a.momenten.some((m) => m.sprintId === sprint.id) && (
              <div className="klein mute">
                📅 In de agenda:{' '}
                {a.momenten.filter((m) => m.sprintId === sprint.id).map((m) => `${m.titel.split(' · ')[0]} ${fmtDag(m.startdatum)}${m.einddatum !== m.startdatum ? ` – ${fmtDag(m.einddatum)}` : ''} ${m.van}${m.sync === 'FOUT' ? ' ⚠' : ''}`).join(' · ')}
              </div>
            )}
          </div>
        )}
        {tab !== 'backlog' && !sprint && <div className="leeg">Nog geen sprint. {kan ? 'Maak er een met “+ Sprint”.' : ''}</div>}

        {tab === 'bord' && sprint && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
            {BORD_KOLOMMEN.map((k) => {
              const kaarten = inSprint.filter((i) => i.status === k);
              return (
                <div key={k} className="kolom" style={{ background: 'var(--lijn-2)', borderRadius: 10, padding: 10, minHeight: 160 }}>
                  <div className="rij"><b className="groei">{ITEM_STATUS_LABEL[k]}</b><span className="klein mute">{kaarten.length} · {fmtUren(kaarten.reduce((s, i) => s + waarde(i), 0))}{e}</span></div>
                  {kaarten.map((i) => {
                    const idx = BORD_KOLOMMEN.indexOf(k);
                    return (
                      <div key={i.id} className="paneel" style={{ padding: 10 }}>
                        <div className="vet">{i.naam}</div>
                        <div className="klein mute">{i.expertise ?? '–'} · {sch(i)}</div>
                        {(i.besteedMin ?? 0) > 0 && <div style={{ marginTop: 4 }}><Stand item={i} klein /></div>}
                        {kan && sprint.status !== 'AFGESLOTEN' ? (
                          <select className="klein" value={i.eigenaarId ?? ''} onChange={(e) => zet.mutate({ id: i.id, body: { eigenaarId: e.target.value || null } })} style={{ marginTop: 6 }}>
                            <option value="">– niemand –</option>
                            {sprint.leden.map((l) => <option key={l.engineerId} value={l.engineerId}>{naam(l.engineerId)}</option>)}
                          </select>
                        ) : (
                          <div className="klein" style={{ marginTop: 4 }}>👷 {naam(i.eigenaarId)}</div>
                        )}
                        {kan && sprint.status === 'LOPEND' && (
                          <div className="rij" style={{ marginTop: 6 }}>
                            <button className="icoon-knop" disabled={idx === 0} onClick={() => zet.mutate({ id: i.id, body: { status: BORD_KOLOMMEN.at(idx - 1) } })} title="Terug">←</button>
                            <div className="groei" />
                            <button className="icoon-knop" disabled={idx === BORD_KOLOMMEN.length - 1} onClick={() => zet.mutate({ id: i.id, body: { status: BORD_KOLOMMEN.at(idx + 1) } })} title="Verder">→</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {tab === 'sprint' && sprint && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
            <div className="kolom">
              <h3>Backlog</h3>
              {backlog.map((i) => (
                <div key={i.id} className="paneel vlak rij">
                  <div className="groei"><b>{i.naam}</b><div className="klein mute">{i.expertise ?? '–'} · {sch(i)}</div></div>
                  {kan && sprint.status !== 'AFGESLOTEN' && <button className="knop klein" onClick={() => zet.mutate({ id: i.id, body: { sprintId: sprint.id } })}>In sprint →</button>}
                </div>
              ))}
              {!backlog.length && <div className="leeg">Backlog is leeg.</div>}
            </div>
            <div className="kolom">
              <h3>Sprint {sprint.nummer}</h3>
              {inSprint.map((i) => (
                <div key={i.id} className="paneel vlak rij">
                  {kan && sprint.status !== 'AFGESLOTEN' && i.status !== 'KLAAR' && <button className="knop klein" onClick={() => zet.mutate({ id: i.id, body: { sprintId: null } })}>← Backlog</button>}
                  <div className="groei"><b>{i.naam}</b><div className="klein mute">{i.expertise ?? '–'} · {sch(i)} · {naam(i.eigenaarId)}</div></div>
                  <Badge toon={STATUS_TOON[i.status]}>{ITEM_STATUS_LABEL[i.status]}</Badge>
                </div>
              ))}
              {!inSprint.length && <div className="leeg">Nog niets in deze sprint.</div>}
            </div>
          </div>
        )}

        {tab === 'backlog' && (
          <table className="tabel">
            <thead><tr><th>#</th><th>Item</th><th>Expertise</th><th className="rechts">Uren</th>{pt && <th className="rechts">Punten</th>}<th>Niveau</th><th /></tr></thead>
            <tbody>
              {backlog.map((i, n) => (
                <tr key={i.id}>
                  <td>{n + 1}</td>
                  <td><b>{i.naam}</b>{i.werkzaamheden && <div className="klein mute">{i.werkzaamheden}</div>}</td>
                  <td>{i.expertise ?? '–'}</td>
                  <td className="rechts">{fmtUren(i.uren)}</td>
                  {pt && <td className="rechts">{i.punten ?? '–'}</td>}
                  <td className="klein">{i.minNiveau ? `≥ ${i.minNiveau}%` : ''}</td>
                  <td className="nowrap">
                    {kan && (
                      <>
                        <button className="icoon-knop" disabled={n === 0} onClick={() => schuif(n, -1)} title="Hoger">˄</button>
                        <button className="icoon-knop" disabled={n === backlog.length - 1} onClick={() => schuif(n, 1)} title="Lager">˅</button>
                        <button className="icoon-knop" onClick={() => setItemForm(i)} title="Bewerken"><Icoon naam="pencil" maat={16} /></button>
                        <VerwijderKnop klein label="🗑" onBevestig={() => verwijderItem.mutate(i.id)} />
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === 'backlog' && !backlog.length && <div className="leeg">Backlog is leeg. {kan ? 'Voeg items toe met “+ Item”.' : ''}</div>}
      </div>
      {itemForm && <TakenpakketForm projectId={a.project.id} tp={itemForm === 'nieuw' ? undefined : itemForm} werkpakketId={a.werkpakket.id} onSluit={() => { setItemForm(null); void q.refetch(); }} />}
      {sprintForm && <SprintForm sprint={sprintForm} onSluit={() => { setSprintForm(null); void q.refetch(); }} />}
      {template && <BacklogUitTemplate wpId={a.werkpakket.id} onSluit={() => { setTemplate(false); void q.refetch(); }} />}
      {rapport?.rapport && <SprintRapport sprint={rapport} r={rapport.rapport} werkpakket={a.werkpakket.naam} project={`${a.project.nummer} · ${a.project.naam}`} onSluit={() => setRapport(null)} />}
      {startSprint && <StartSprint sprint={startSprint} standup={a.werkpakket.standup} onSluit={(ok) => { setStartSprint(null); if (ok) setTab('bord'); void q.refetch(); }} />}
      {afsluiten && <Afsluiten sprint={afsluiten} open={a.items.filter((i) => i.sprintId === afsluiten.id && i.status !== 'KLAAR').length} heeftVolgende={a.sprints.some((s) => s.status === 'GEPLAND')} onSluit={() => { setAfsluiten(null); void q.refetch(); }} />}
    </div>
  );
}

function SprintForm({ sprint, onSluit }: { sprint: Sprint; onSluit: () => void }) {
  const { data: d } = useData();
  const [f, setF] = useState({ start: sprint.start, eind: sprint.eind, doel: sprint.doel ?? '', leden: sprint.leden });
  const opslaan = useActie(() => api.put(`/api/sprints/${sprint.id}`, { ...f, doel: f.doel || null }), { succes: 'Sprint opgeslagen', opNa: onSluit });
  if (!d) return null;
  const lid = (id: string) => f.leden.find((l) => l.engineerId === id);
  return (
    <Modal titel={`Sprint ${sprint.nummer}`} onSluit={onSluit} voet={<><button className="knop" onClick={onSluit}>Annuleren</button><button className="knop primair" onClick={() => opslaan.mutate()}>Opslaan</button></>}>
      <div className="grid2">
        <Veld label="Start"><input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} /></Veld>
        <Veld label="Eind"><input type="date" value={f.eind} onChange={(e) => setF({ ...f, eind: e.target.value })} /></Veld>
        <Veld label="Sprintdoel" className="span2"><input value={f.doel} onChange={(e) => setF({ ...f, doel: e.target.value })} placeholder="Wat moet er aan het eind werken?" /></Veld>
      </div>
      <h3 style={{ margin: '14px 0 8px' }}>Team</h3>
      <div className="grid2">
        {d.engineers.filter((e) => e.actief !== false).map((e) => (
          <div className="rij" key={e.id}>
            <label className="check groei">
              <input type="checkbox" checked={!!lid(e.id)} onChange={(ev) => setF({ ...f, leden: ev.target.checked ? [...f.leden, { engineerId: e.id, inzetPct: 100 }] : f.leden.filter((l) => l.engineerId !== e.id) })} />
              {e.naam}
            </label>
            {lid(e.id) && (
              <select value={lid(e.id)!.inzetPct} style={{ width: 110 }} onChange={(ev) => setF({ ...f, leden: f.leden.map((l) => (l.engineerId === e.id ? { ...l, inzetPct: Number(ev.target.value) } : l)) })}>
                {[100, 80, 60, 50, 40, 20].map((p) => <option key={p} value={p}>{p}% inzet</option>)}
              </select>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}

function Afsluiten({ sprint, open, heeftVolgende, onSluit }: { sprint: Sprint; open: number; heeftVolgende: boolean; onSluit: () => void }) {
  const [nietAf, setNietAf] = useState<'BACKLOG' | 'VOLGENDE'>(heeftVolgende ? 'VOLGENDE' : 'BACKLOG');
  const [notitie, setNotitie] = useState('');
  const sluit = useActie(() => api.post<{ verplaatst: number; naar: string }>(`/api/sprints/${sprint.id}/afsluiten`, { nietAf, notitie: notitie || null }), {
    succes: (r) => `Sprint ${sprint.nummer} afgesloten${r.verplaatst ? `; ${r.verplaatst} item(s) naar ${r.naar}` : ''}`,
    opNa: onSluit,
  });
  return (
    <Modal titel={`Sprint ${sprint.nummer} afsluiten`} onSluit={onSluit} voet={<><button className="knop" onClick={onSluit}>Annuleren</button><button className="knop primair" onClick={() => sluit.mutate()}>Afsluiten</button></>}>
      <p>{open ? `${open} item(s) zijn nog niet klaar.` : 'Alle items zijn klaar.'}</p>
      {open > 0 && (
        <Veld label="Wat gebeurt er met de items die niet klaar zijn?">
          <select value={nietAf} onChange={(e) => setNietAf(e.target.value as 'BACKLOG' | 'VOLGENDE')}>
            <option value="BACKLOG">Terug naar de backlog</option>
            <option value="VOLGENDE" disabled={!heeftVolgende}>Mee naar de volgende sprint{heeftVolgende ? '' : ' (maak eerst een sprint)'}</option>
          </select>
        </Veld>
      )}
      <Veld label="Notitie (review / retro, optioneel)"><textarea value={notitie} onChange={(e) => setNotitie(e.target.value)} /></Veld>
    </Modal>
  );
}

/** Sprint starten, met de keuze om de sprintmomenten in de agenda te zetten (SPEC §9.6). */
function StartSprint({ sprint, standup, onSluit }: { sprint: Sprint; standup: boolean; onSluit: (ok: boolean) => void }) {
  const [agenda, setAgenda] = useState(sprint.leden.length > 0);
  const start = useActie(() => api.post<{ momenten: number }>(`/api/sprints/${sprint.id}/start`, { agenda }), {
    succes: (r) => (r.momenten ? `Sprint gestart; ${r.momenten} afspraak/afspraken gaan naar de agenda` : 'Sprint gestart'),
    opNa: () => onSluit(true),
  });
  return (
    <Modal titel={`Sprint ${sprint.nummer} starten`} onSluit={() => onSluit(false)} voet={<><button className="knop" onClick={() => onSluit(false)}>Annuleren</button><button className="knop primair" disabled={start.isPending} onClick={() => start.mutate()}>Starten</button></>}>
      <div className="kolom">
        <p>Vanaf nu kunnen teamleden items oppakken in <i>Mijn taken</i>.</p>
        <label className="check">
          <input type="checkbox" checked={agenda} disabled={!sprint.leden.length} onChange={(e) => setAgenda(e.target.checked)} /> Sprintmomenten in de agenda van het team zetten
        </label>
        <div className="klein mute" style={{ marginLeft: 24 }}>
          Sprintplanning op {fmtDag(sprint.start)} 09:00–10:00 en sprintreview en retro op de laatste werkdag 15:00–16:00
          {standup ? ', plus dagelijks een stand-up van 09:00 tot 09:15' : ''}. Eén uitnodiging voor het hele team, met Teams of Meet als de agenda dat kan.
          {!standup && ' De stand-up zet je aan bij het werkpakket.'}
          {!sprint.leden.length && ' Kies eerst een team via “Team en data”.'}
        </div>
      </div>
    </Modal>
  );
}

/** SPEC §9.2 – burndown: resterende uren per werkdag tegen de ideale lijn. */
function Burndown({ data, e = 'u' }: { data: Sprint['burndown']; e?: string }) {
  const B = 600, H = 150, L = 34, R = 8, T = 10, O = 22;
  const n = data.punten.length;
  if (n < 2) return null;
  const x = (i: number) => L + (i * (B - L - R)) / (n - 1);
  const y = (u: number) => T + (1 - u / Math.max(1, data.totaal)) * (H - T - O);
  const ideaal = data.punten.map((p, i) => `${x(i)},${y(p.ideaal)}`).join(' ');
  const echt = data.punten.map((p, i) => (p.rest == null ? null : `${x(i)},${y(p.rest)}`)).filter(Boolean).join(' ');
  const stap = Math.max(1, Math.ceil(n / 8));
  const laatste = [...data.punten].reverse().find((p) => p.rest != null);
  return (
    <div>
      <div className="rij klein"><b className="groei">Burndown</b><span className="mute">— ideaal · ━ nog te doen{laatste ? ` (${fmtUren(laatste.rest ?? 0)}${e})` : ''}</span></div>
      <svg viewBox={`0 0 ${B} ${H}`} style={{ width: '100%', maxHeight: 180 }} role="img" aria-label="Burndown van de sprint">
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={L} x2={B - R} y1={y(data.totaal * f)} y2={y(data.totaal * f)} stroke="var(--lijn-2)" />
            <text x={L - 4} y={y(data.totaal * f) + 4} fontSize="10" textAnchor="end" fill="var(--mute)">{fmtUren(data.totaal * f)}{e}</text>
          </g>
        ))}
        {data.punten.map((p, i) => (i % stap === 0 || i === n - 1 ? <text key={p.dag} x={x(i)} y={H - 6} fontSize="10" textAnchor="middle" fill="var(--mute)">{fmtDag(p.dag)}</text> : null))}
        <polyline points={ideaal} fill="none" stroke="var(--lijn)" strokeWidth="2" strokeDasharray="5 4" />
        {echt && <polyline points={echt} fill="none" stroke="var(--accent)" strokeWidth="2.5" />}
        {data.punten.map((p, i) => (p.rest == null ? null : <circle key={p.dag} cx={x(i)} cy={y(p.rest)} r="3" fill="var(--accent)"><title>{`${fmtDag(p.dag)}: nog ${fmtUren(p.rest)}${e}`}</title></circle>))}
      </svg>
    </div>
  );
}

/** SPEC §9.7 – backlog vullen uit werkpakket-templates. */
function BacklogUitTemplate({ wpId, onSluit }: { wpId: string; onSluit: () => void }) {
  const { data: d } = useData();
  const [gekozen, setGekozen] = useState<string[]>([]);
  const toepassen = useActie(() => api.post<{ items: number }>(`/api/agile/${wpId}/template`, { templateIds: gekozen }), { succes: (r) => `${r.items} item(s) aan de backlog toegevoegd`, opNa: onSluit });
  if (!d) return null;
  const wps = d.templates.filter((t) => t.soort === 'WERKPAKKET').sort((a, b) => a.volgorde - b.volgorde);
  return (
    <Modal titel="Backlog uit template" onSluit={onSluit} voet={<><button className="knop" onClick={onSluit}>Annuleren</button><button className="knop primair" disabled={!gekozen.length || toepassen.isPending} onClick={() => toepassen.mutate()}>Toevoegen</button></>}>
      <p className="mute klein">De takenpakketten van de gekozen templates komen onderaan de backlog, met naam, expertise, uren en kennisniveau.</p>
      {wps.map((t) => (
        <label key={t.id} className="paneel vlak check" style={{ display: 'flex', marginBottom: 6 }}>
          <input type="checkbox" checked={gekozen.includes(t.id)} onChange={(e) => setGekozen(e.target.checked ? [...gekozen, t.id] : gekozen.filter((x) => x !== t.id))} />
          <div>
            <b>{t.naam}</b>
            <div className="klein mute">{d.templates.filter((x) => x.ouderId === t.id).map((x) => `${x.naam} (${fmtUren(x.uren)}u)`).join(', ') || 'geen takenpakketten'}</div>
          </div>
        </label>
      ))}
      {!wps.length && <div className="leeg">Nog geen templates. Maak ze bij Beheer › Templates.</div>}
    </Modal>
  );
}

/** SPEC §9.7 – rapport per sprint (momentopname bij afsluiten), af te drukken. */
function SprintRapport({ sprint, r, werkpakket, project, onSluit }: { sprint: Sprint; r: Rapport; werkpakket: string; project: string; onSluit: () => void }) {
  const e = r.eenheid === 'PUNTEN' ? 'pt' : 'u';
  const pct = r.gepland ? Math.round((r.klaar / r.gepland) * 100) : 0;
  return (
    <Modal titel={`Sprintrapport · sprint ${sprint.nummer}`} onSluit={onSluit} voet={<><button className="knop" onClick={() => window.print()}><Icoon naam="printer" maat={16} /> Afdrukken</button><button className="knop primair" onClick={onSluit}>Sluiten</button></>}>
      <div className="kolom" style={{ gap: 10 }}>
        <div>
          <div className="klein mute">{project} · {werkpakket}</div>
          <h2 style={{ margin: '2px 0' }}>Sprint {sprint.nummer}: {fmtDag(sprint.start, 'jaar')} – {fmtDag(sprint.eind, 'jaar')}</h2>
          <div className="klein">Afgesloten op {fmtDag(r.afgesloten, 'jaar')}{r.doel ? ` · Doel: ${r.doel}` : ''}</div>
        </div>
        <div className="kaartjes">
          <div className="kaartje"><div className="getal">{fmtUren(r.klaar)} / {fmtUren(r.gepland)}{e}</div><div className="lbl">klaar ({pct}%)</div></div>
          <div className="kaartje"><div className="getal">{r.klaarItems.length} / {r.klaarItems.length + r.nietAf.length}</div><div className="lbl">items klaar</div></div>
          <div className="kaartje"><div className="getal">{r.capaciteitUren != null ? `${fmtUren(r.capaciteitUren)}u` : '–'}</div><div className="lbl">capaciteit team</div></div>
        </div>
        <div className="klein"><b>Team:</b> {r.team.map((t) => `${t.naam}${t.inzetPct < 100 ? ` (${t.inzetPct}%)` : ''}`).join(', ') || '–'}</div>
        {r.burndown && r.burndown.totaal > 0 && <Burndown data={r.burndown} e={e} />}
        <div>
          <b>Klaar</b>
          <ul style={{ margin: '4px 0' }}>{r.klaarItems.map((i) => <li key={i.naam}>{i.naam} · {fmtUren(i.waarde)}{e}</li>)}</ul>
          {!r.klaarItems.length && <div className="klein mute">Niets afgerond.</div>}
        </div>
        {r.nietAf.length > 0 && (
          <div>
            <b>Niet af → {r.naar}</b>
            <ul style={{ margin: '4px 0' }}>{r.nietAf.map((i) => <li key={i.naam}>{i.naam} · {fmtUren(i.waarde)}{e} · {ITEM_STATUS_LABEL[i.status as ItemStatus] ?? i.status}</li>)}</ul>
          </div>
        )}
        {r.notitie && (
          <div>
            <b>Review en retro</b>
            <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0' }}>{r.notitie}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}
